import React, { useState, useRef, useEffect, useCallback } from "react";
import styled from "styled-components";
import GroupSelection from "./GroupSelection";
import SubgroupSelection from "./SubgroupSelection";
import StyleName from "./StyleName";
import TopBar from "./TopBar";
import Scrollbar from "../../scroll/Scrollbar";
import {
    MELOGY_ALMOST_OPAQUE,
    MELOGY_TRANSPARENT,
    MELOGY_WHITE,
    MELOGY_BLACK,
} from "../../../style/colors";
import {
    FONT_SIZE_2XS,
    TITLE_FONT_FAMILY,
    TITLE_FONT_WEIGHT,
    TITLE_LETTER_SPACING,
} from "../../../style/fontSettings";
import { POPOVER_SHADOW } from "../../../style/shadows";
import StyleSetting from "./StyleSetting";
import { 
    findFarthestColor, 
    getStyleColor,
} from '../../../util/StyleSettingsUtil';

const Overlay = styled.div`
    position: fixed;
    top: 110px;
    left: 0;
    width: 100%;
    height: calc(100vh - 220px);
    background-color: ${MELOGY_TRANSPARENT};
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
`;

const OverlayContentBackground = styled.div`
    width: 100%;
    max-width: 960px;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const OverlayContent = styled.div`
    background-color: ${({ backgroundColor }) => (backgroundColor ? backgroundColor : "#000")};
    width: 80%;
    height: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-direction: column;
    position: relative;
`;

const StyleSettingContainerOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: calc(100vh - 212px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 500;
    background-color: ${MELOGY_TRANSPARENT};
`;

const StyleSettingContainer = styled.div`
    width: 100%;
    max-width: 960px;
    background-color: ${MELOGY_TRANSPARENT};
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow-y: auto;
    position: absolute; 
    z-index: 501;
    padding: 8px;
`;

const StyleSettingBackground = styled.div`
    width: ${({ isColorPicker }) => (isColorPicker ? "225px" : "calc(80% - 64px)")};
    padding: 16px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: ${MELOGY_ALMOST_OPAQUE};
    box-shadow: ${POPOVER_SHADOW};
`;

const GroupTitle = styled.div`
    width: 100%;
    height: 32px;
    font-size: ${FONT_SIZE_2XS};
    font-family: ${TITLE_FONT_FAMILY};
    font-weight: ${TITLE_FONT_WEIGHT};
    letter-spacing: ${TITLE_LETTER_SPACING};
    text-transform: uppercase;
    display: flex;
    justify-content: center;
    align-items: center;
    color: ${MELOGY_WHITE};
    user-select: none;
`;

const StyleSettings = ({ styles, selectedStyle, closeOverlay, setSelectedStyle, setStyleSettingsIsOpen, updateStyle }) => {
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [subgroupsOfSelectedGroup, setSubgroupsOfSelectedGroups] = useState([]);
    const [selectedSubgroup, setSelectedSubgroup] = useState(null);
    const [selectedSettings, setSelectedSettings] = useState([]);
    const [displayValues, setDisplayValues] = useState(
        selectedStyle.reduce((acc, setting) => {
            acc[setting.name] = setting.value;
            return acc;
        }, {})
    );
    const [subgroupSelectionPopoverIsOpen, setSubgroupSelectionPopoverIsOpen] = useState(false);
    const [settingsPopoverIsOpen, setSettingsPopoverIsOpen] = useState(false);
    const styleSettingRef = useRef(null);
    const overlayRef = useRef(null);
    const overlayContentRef = useRef(null);
    const backgroundColor = getStyleColor(selectedStyle); 
    const buttonColor = findFarthestColor(backgroundColor);
    const color = buttonColor === "white" ? MELOGY_WHITE : MELOGY_BLACK;

    const updateSelectedSettings = useCallback(() => {
        const selectedSettings = selectedStyle.filter(setting => 
            setting.group === selectedGroup && (!setting.subgroup || setting.subgroup === selectedSubgroup)
        );
        setSelectedSettings(selectedSettings);
    }, [selectedStyle, selectedGroup, selectedSubgroup]);

    useEffect(() => {
        updateSelectedSettings();
    }, [updateSelectedSettings]); 

    useEffect(() => {
        const handleClickOutside = (event) => {
            const clickedInsideOverlay = overlayRef.current?.contains(event.target);
            const clickedOutsideOverlayContent = !overlayContentRef.current?.contains(event.target);
            const clickedOutsideStyleSetting = !styleSettingRef.current?.contains(event.target);
    
            const shouldUnselectStyle = 
                clickedInsideOverlay &&
                clickedOutsideOverlayContent &&
                !settingsPopoverIsOpen &&
                !subgroupSelectionPopoverIsOpen;
    
            const shouldCloseSettingsPopover = 
                clickedInsideOverlay &&
                clickedOutsideStyleSetting &&
                settingsPopoverIsOpen;
    
            if (shouldUnselectStyle) {
                handleUnselectStyle();
            }
    
            if (shouldCloseSettingsPopover) {
                setSettingsPopoverIsOpen(false);
                setSubgroupSelectionPopoverIsOpen(subgroupsOfSelectedGroup.length > 0);
            }
        };
    
        document.addEventListener("mousedown", handleClickOutside);
        
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [settingsPopoverIsOpen, subgroupSelectionPopoverIsOpen, subgroupsOfSelectedGroup]);    

    const handleUnselectStyle = () => {
        setStyleSettingsIsOpen(false);
        setSelectedStyle(null);
        closeOverlay();
    };

    const handleSelectGroup = (groupName) => {
        console.log("groupName", groupName);
        const subgroupsOfSelectedGroup = getSubgroupsByGroup(groupName);

        if (subgroupsOfSelectedGroup.length > 0) {
            setSubgroupsOfSelectedGroups(subgroupsOfSelectedGroup);
            setSubgroupSelectionPopoverIsOpen(true);
        } else {
            setSubgroupsOfSelectedGroups([]);
            setSubgroupSelectionPopoverIsOpen(false);
            setTimeout(() => {
                setSettingsPopoverIsOpen(true);
            }, 25);
        }
        setSelectedGroup(groupName);
    };

    const getSubgroupsByGroup = (groupName) => {
        const subgroups = new Set();
        selectedStyle.forEach(item => {
            if (item.group === groupName && item.subgroup) {
                subgroups.add(item.subgroup);
            }
        });
        return Array.from(subgroups);
    };

    const handleSelectSubgroup = (subgroup) => {
        setSelectedSubgroup(subgroup);
        updateSelectedSettings();
        setTimeout(() => {
            setSettingsPopoverIsOpen(true);
            setSubgroupSelectionPopoverIsOpen(false);
        }, 25);
    };

    const updateStyleSettings = (settingName, newValue) => {
        updateStyle(settingName, newValue);
    };

    const setSliderDisplayValue = (settingName, newDisplayValue) => {
        setDisplayValues((prev) => ({
            ...prev,
            [settingName]: newDisplayValue,
        }));
    };

    const content = (
        <>
            {selectedSettings.map((styleSetting) => (
                <StyleSetting
                    key={styleSetting.name}
                    styleSetting={styleSetting}
                    updateStyleSettings={updateStyleSettings}
                    sliderDisplayValue={displayValues[styleSetting.name]}
                    setSliderDisplayValue={(value) => setSliderDisplayValue(styleSetting.name, value)}
                />
            ))}
        </>
    );

    return (
        <Overlay ref={overlayRef}>
            <OverlayContentBackground>
                <OverlayContent 
                    ref={overlayContentRef}
                    backgroundColor={backgroundColor}
                >
                    <TopBar
                        buttonColor={buttonColor}
                        onClose={handleUnselectStyle}
                    />
                    <StyleName 
                        styles={styles}
                        color={color}
                        selectedStyle={selectedStyle}
                        updateStyle={updateStyle}
                    />
                    <GroupSelection 
                        selectedStyle={selectedStyle} 
                        handleSelectGroup={handleSelectGroup}
                    />
                    {settingsPopoverIsOpen && (
                        <StyleSettingContainerOverlay>
                            <StyleSettingContainer>
                                <StyleSettingBackground 
                                    ref={styleSettingRef}
                                    isColorPicker={selectedGroup === 'Color'} 
                                >
                                    <GroupTitle>{selectedSubgroup ? selectedSubgroup : selectedGroup}</GroupTitle>
                                    {selectedSettings.length > 4 ? (
                                        <Scrollbar>{content}</Scrollbar>
                                    ) : (
                                        content
                                    )}
                                </StyleSettingBackground>
                            </StyleSettingContainer>
                        </StyleSettingContainerOverlay>
                    )}
                </OverlayContent>
            </OverlayContentBackground>
            {subgroupSelectionPopoverIsOpen && (
                <SubgroupSelection 
                    selectedGroup={selectedGroup}
                    subgroups={subgroupsOfSelectedGroup}
                    onSelectSubgroup={handleSelectSubgroup}
                    onClose={() => {
                        setSubgroupSelectionPopoverIsOpen(false);
                        setSelectedSubgroup(null);
                    }}
                />
            )}
        </Overlay>
    );
};

export default StyleSettings;